import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import { useError } from '../../contexts/ErrorContext';

interface Church {
  id: number;
  name: string;
}

interface SignUpFormInputs {
  church_id: string;
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

interface InviteValidation {
  valid: boolean;
  roleName?: string;
  churchId?: number;
  reason?: string;
}

const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<SignUpFormInputs>();
  const { setError } = useError();

  const [churches, setChurches] = useState<Church[]>([]);
  const [inviteCode, setInviteCode] = useState(searchParams.get('invite') || '');
  const [inviteValidated, setInviteValidated] = useState(false);
  const [inviteRole, setInviteRole] = useState<string>('');
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Fetch churches on mount
  useEffect(() => {
    const fetchChurches = async () => {
      try {
        const response = await apiClient.get<Church[]>('/users/available-churches');
        setChurches(response.data);
      } catch (error) {
        console.error('Failed to fetch churches:', error);
      }
    };
    fetchChurches();
  }, []);

  // Auto-validate if invite code is in URL
  useEffect(() => {
    const urlInvite = searchParams.get('invite');
    if (urlInvite) {
      validateInviteCode(urlInvite);
    }
  }, [searchParams]);

  const validateInviteCode = async (code: string) => {
    if (!code.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setValidatingInvite(true);
    try {
      const response = await apiClient.get<InviteValidation>(`/invites/validate/${code}`);
      if (response.data.valid) {
        setInviteValidated(true);
        setInviteRole(response.data.roleName || 'viewer');
        if (response.data.roleName === 'staff' && response.data.churchId) setValue('church_id', String(response.data.churchId));
        toast.success('Invite code validated!');
      } else {
        setInviteValidated(false);
        toast.error(response.data.reason || 'Invalid invite code');
      }
    } catch (error: any) {
      setInviteValidated(false);
      const message = error.response?.data?.reason || 'Invalid invite code';
      toast.error(message);
    } finally {
      setValidatingInvite(false);
    }
  };

  const onSubmit: SubmitHandler<SignUpFormInputs> = async (data) => {
    if (!inviteValidated) {
      toast.error('Please validate your invite code first');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/users/register', {
        ...data,
        inviteCode: inviteCode.trim(),
      });

      toast.success('Registration successful! Please log in.');
      navigate('/login');
    } catch (error: any) {
      const message = error.response?.data?.message ||
                      error.response?.data?.error ||
                      'Registration failed. Please try again.';
      toast.error(message);
      setError(error as Error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-5" style={{ maxWidth: '500px' }}>
      <h2 className="mb-4 text-center">Sign Up</h2>

      {/* Invite Code Section */}
      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Step 1: Enter Invite Code</h5>
          <p className="text-muted small">
            You need an invite code to register. Contact your church administrator if you don't have one.
          </p>

          <div className="input-group">
            <input
              type="text"
              className={`form-control ${inviteValidated ? 'is-valid' : ''}`}
              placeholder="Enter your invite code"
              value={inviteCode}
              onChange={(e) => {
                setInviteCode(e.target.value);
                setInviteValidated(false);
              }}
              disabled={inviteValidated}
            />
            <button
              type="button"
              className={`btn ${inviteValidated ? 'btn-success' : 'btn-primary'}`}
              onClick={() => validateInviteCode(inviteCode)}
              disabled={validatingInvite || inviteValidated}
            >
              {validatingInvite ? 'Checking...' : inviteValidated ? 'Valid' : 'Validate'}
            </button>
          </div>

          {inviteValidated && (
            <div className="mt-2">
              <span className="badge bg-success">
                You will be registered as: {inviteRole}
              </span>
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => {
                  setInviteValidated(false);
                  setInviteCode('');
                }}
              >
                Use different code
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Registration Form - Only shown after invite validated */}
      {inviteValidated && (
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Step 2: Create Your Account</h5>

            <form onSubmit={handleSubmit(onSubmit)}>
              {inviteRole === 'staff' ? <div className="mb-3">
                <p>Staff scheduling at St. Luke’s / San Lucas</p>
                <input type="hidden" {...register('church_id')} />
              </div> : <>
              <div className="mb-3">
                <label className="form-label">Church</label>
                <select
                  {...register("church_id", { required: "Church is required" })}
                  className={`form-select ${errors.church_id ? 'is-invalid' : ''}`}
                >
                  <option value="">Select your church</option>
                  {churches.map((church) => (
                    <option key={church.id} value={church.id}>{church.name}</option>
                  ))}
                </select>
                {errors.church_id && <div className="invalid-feedback">{errors.church_id.message}</div>}
              </div>

              </>}

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    {...register("first_name", { required: "First name is required" })}
                    className={`form-control ${errors.first_name ? 'is-invalid' : ''}`}
                    placeholder="First Name"
                  />
                  {errors.first_name && <div className="invalid-feedback">{errors.first_name.message}</div>}
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    {...register("last_name", { required: "Last name is required" })}
                    className={`form-control ${errors.last_name ? 'is-invalid' : ''}`}
                    placeholder="Last Name"
                  />
                  {errors.last_name && <div className="invalid-feedback">{errors.last_name.message}</div>}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  {...register("username", { required: "Username is required" })}
                  className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                  placeholder="Choose a username"
                />
                {errors.username && <div className="invalid-feedback">{errors.username.message}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address"
                    }
                  })}
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  placeholder="your.email@example.com"
                />
                {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
              </div>

              <div className="mb-3">
                <label className="form-label">Password</label>
                <input
                  type="password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters"
                    }
                  })}
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  placeholder="At least 8 characters"
                />
                {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
              </div>

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={submitting}
              >
                {submitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="text-center mt-3">
        <span className="text-muted">Already have an account? </span>
        <a href="/login" className="text-decoration-none">Log in</a>
      </div>
    </div>
  );
};

export default SignUp;
